import * as React from "react";
import { cn } from "@finster/ui";
import { X } from "lucide-react";

interface AttachmentChipProps {
  icon: React.ReactNode;
  label: string;
  description?: string;
  onRemove: () => void;
  disabled?: boolean;
  ariaLabel?: string;
  removeAriaLabel?: string;
  className?: string;
}

/**
 * Presentational chip used for composer attachments.
 *
 * Attachment-specific state and removal semantics stay with the caller; this
 * primitive owns the shared pill layout and accessible remove control.
 */
export const AttachmentChip: React.FC<AttachmentChipProps> = ({
  icon,
  label,
  description,
  onRemove,
  disabled = false,
  ariaLabel = description ? `${label}. ${description}` : undefined,
  removeAriaLabel = description ? `Remove ${label} (${description})` : `Remove ${label}`,
  className,
}) => (
  <div
    className={cn(
      "flex min-w-0 max-w-full shrink-0 items-center gap-1.5",
      "min-h-8 rounded-full border border-border bg-background px-3 py-1 shadow-xs",
      className
    )}
    aria-label={ariaLabel}
    aria-disabled={disabled || undefined}
  >
    <span
      className="flex size-4 shrink-0 items-center justify-center text-foreground [&>svg]:size-4"
      aria-hidden
    >
      {icon}
    </span>
    <span className="flex min-w-0 max-w-[150px] flex-col">
      <span className="truncate text-xs font-medium text-foreground" title={label}>
        {label}
      </span>
      {description && (
        <span className="truncate text-[10px] text-muted-foreground" title={description}>
          {description}
        </span>
      )}
    </span>
    <button
      type="button"
      disabled={disabled}
      aria-label={removeAriaLabel}
      onClick={onRemove}
      className={cn(
        "flex shrink-0 cursor-pointer items-center justify-center rounded-sm border-0 bg-transparent p-0",
        "text-foreground hover:text-foreground/80",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus focus-visible:ring-offset-2",
        disabled && "pointer-events-none opacity-50"
      )}
    >
      <X className="h-4 w-4" aria-hidden />
    </button>
  </div>
);
